/**
 * @fileoverview RiddleCover - Portada Promocional a Pantalla Completa para Adivina Adivinador (Riddle - Tile 10).
 * Diseñada para pantallas gigantes de estadio (Full HD 1920x1080 y 4K 3840x2160).
 * Soporta fondos de imagen SkiaSharp / sólidos dinámicos, títulos de alto contraste,
 * branding institucional (ADSL) y logotipos de patrocinador.
 * 
 * @module components/Screen/Sections/Riddle/RiddleCover
 */

import React, { useMemo } from 'react';

/**
 * Resuelve la URL absoluta de una imagen o recurso.
 * @param {string} url - URL relativa o absoluta.
 * @returns {string} URL resuelta.
 */
const resolveMediaUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:') || url.startsWith('data:')) {
        return url;
    }
    const apiBase = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
    return `${apiBase}${url.startsWith('/') ? url.substring(1) : url}`;
};

/**
 * Componente de Portada Promocional de Adivina Adivinador para Pantalla Gigante.
 * 
 * @param {Object} props
 * @param {Object} [props.data] - Configuración y metadatos de la portada emitidos por WS/API.
 * @param {boolean} [props.isActiveWomensMode] - Indicador de modo Femenil o Varonil para fondos por defecto.
 * @returns {React.ReactElement}
 */
export const RiddleCover = ({ data, isActiveWomensMode }) => {
    const title = data?.title || data?.headerTitle || 'TRIVIA DOMINGUERA';
    const subtitle = data?.subtitle || '¿QUIÉN SE ESCONDE EN LA SOMBRA?';
    const brandLogoUrl = resolveMediaUrl(data?.brandLogoUrl) || '/img/page/adsl-five.png';
    const sponsorLogoUrl = resolveMediaUrl(data?.sponsorLogoUrl);
    const fontFamily = data?.fontFamily ? `"${data.fontFamily}", "GFS Didot", serif` : '"GFS Didot", serif';
    const textColor = data?.textColor || '#FFFFFF';

    // Manejo de fondo dinámico (color sólido, imagen temática o institucional)
    const backgroundStyle = useMemo(() => {
        if (data?.backgroundType === 'image' && data?.backgroundImageUrl) {
            return {
                backgroundImage: `url(${resolveMediaUrl(data.backgroundImageUrl)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat'
            };
        }
        if (data?.backgroundType === 'color' && data?.backgroundColor) {
            return {
                backgroundColor: data.backgroundColor
            };
        }
        return {};
    }, [data?.backgroundType, data?.backgroundImageUrl, data?.backgroundColor]);

    const hasCustomBg = !!(data?.backgroundImageUrl || data?.backgroundColor);

    return (
        <div className="RiddleCover__Screen" style={backgroundStyle}>
            {/* Fondo de respaldo institucional cuando no hay personalización */}
            {!hasCustomBg && (
                <div className="RiddleCover__Screen__DefaultBackground">
                    {isActiveWomensMode ? (
                        <img
                            src="/img/page/backgrounds/barrio9/background-1.png"
                            alt="Fondo Femenil"
                            className="RiddleCover__Screen__DefaultBackground__Image"
                        />
                    ) : (
                        <img
                            src="/img/selfiefan/Fondo-SelfieFan-Varonil-s2-1.png"
                            alt="Fondo Varonil"
                            className="RiddleCover__Screen__DefaultBackground__Image"
                        />
                    )}
                </div>
            )}

            {/* Capa de destellos y ambientación para pantalla gigante */}
            <div className="RiddleCover__Screen__AmbientGlow" />

            {/* Contenedor central de alto impacto */}
            <main className="RiddleCover__Screen__Content">
                {/* Cabecera de Marcas / Patrocinadores */}
                <div className="RiddleCover__Screen__Branding">
                    <img
                        src={brandLogoUrl}
                        alt="Logotipo Principal"
                        className="RiddleCover__Screen__BrandLogo"
                    />

                    {sponsorLogoUrl && (
                        <div className="RiddleCover__Screen__SponsorBadge">
                            <span className="RiddleCover__Screen__Presents" style={{ fontFamily }}>
                                PRESENTA
                            </span>
                            <img
                                src={sponsorLogoUrl}
                                alt="Patrocinador Oficial"
                                className="RiddleCover__Screen__SponsorLogo"
                            />
                        </div>
                    )}
                </div>

                {/* Tipografías y Título Promocional */}
                <div className="RiddleCover__Screen__Titles">
                    {!sponsorLogoUrl && (
                        <p className="RiddleCover__Screen__PresentsStandalone" style={{ fontFamily, color: textColor }}>
                            PRESENTA
                        </p>
                    )}

                    <h1
                        className="RiddleCover__Screen__MainTitle"
                        style={{ fontFamily, color: textColor }}
                    >
                        {title}
                    </h1>

                    {/* <p
                        className="RiddleCover__Screen__Subtitle"
                        style={{ fontFamily, color: textColor }}
                    >
                        {subtitle}
                    </p> */}
                </div>

                {/* Mascotas / Gráficos de estadio */}
                <div className="RiddleCover__Screen__Mascots">
                    <img
                        src="/img/games/riddle/male/july-17-2026/lucho.png"
                        alt="Lucho"
                        className="RiddleCover__Screen__Mascot RiddleCover__Screen__Mascot--Lucho"
                    />
                    <div className="RiddleCover__Screen__MascotDivider">
                        <i className="bi bi-question-diamond-fill" />
                    </div>
                    <img
                        src="/img/games/riddle/male/july-17-2026/tunita.png"
                        alt="Tunita"
                        className="RiddleCover__Screen__Mascot RiddleCover__Screen__Mascot--Tunita"
                    />
                </div>
            </main>

            {/* Pie de pantalla institucional */}
            <footer className="RiddleCover__Screen__Footer FooterCredits">
                Hecho con ♥ por Interaktia | 2026
            </footer>
        </div>
    );
};

export default RiddleCover;
